import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Avatar, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button, 
  TextField,
  Tooltip,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import CodeIcon from '@mui/icons-material/Code';
import BrushIcon from '@mui/icons-material/Brush';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import LandscapeIcon from '@mui/icons-material/Landscape';
import PaletteIcon from '@mui/icons-material/Palette';
import HomeIcon from '@mui/icons-material/Home';
import ArchitectureIcon from '@mui/icons-material/Architecture';
// import YouTubeIcon from '@mui/icons-material/YouTube'; // Removed
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'; // Added
import GavelIcon from '@mui/icons-material/Gavel'; // Added
import AssessmentIcon from '@mui/icons-material/Assessment'; // Added
import LocationCityIcon from '@mui/icons-material/LocationCity'; // Added
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; // Added
import { supabase, ensureCustomAgentsTableExists } from '../supabaseClient';

// Define agent icons mapping (Updated for Real Estate + Visual Editor)
const agentIcons = {
  'Görsel Düzenleyici': <BrushIcon />, // Added icon for the new agent
  'Emlak Danışmanı': <HomeIcon />,
  'Konut Kredisi Uzmanı': <AccountBalanceIcon />,
  'Tapu ve Kadastro Uzmanı': <DescriptionIcon />, // Keep DescriptionIcon
  'Gayrimenkul Değerleme Uzmanı': <AssessmentIcon />,
  'Kentsel Dönüşüm Danışmanı': <LocationCityIcon />,
  'Yatırım Danışmanı (Gayrimenkul)': <TrendingUpIcon />,
  'Emlak Hukuku Danışmanı': <GavelIcon />,
  // Add icons for any other predefined agents if needed
};

// Define agent types and models
const AGENT_TYPE_TEXT = 'text';
const AGENT_TYPE_IMAGE = 'image'; // Default if not specified
const TEXT_MODEL = 'gemini-2.0-flash'; // Updated model name to fix quota issue
const IMAGE_MODEL = 'gemini-2.0-flash-exp-image-generation'; // Existing image model

// Updated Predefined Agents for Real Estate (Turkish) + Visual Editor
const predefinedAgents = [
  {
    name: 'Görsel Düzenleyici',
    type: AGENT_TYPE_IMAGE, // Use image generation type
    model: IMAGE_MODEL,     // Use image generation model
    systemInstruction: `Sen bir görsel düzenleme asistanısın. Kullanıcıların yüklediği görselleri düzenle veya metin açıklamalarına göre yeni görseller oluştur.
    - Görselleri düzenleyebilirsin
    - Yeni görseller oluşturabilirsin
    - Görsel stillerini değiştirebilirsin
    - Görsel boyutlarını ayarlayabilirsin`,
    description: 'Mevcut görselleri düzenler veya açıklamalardan yeni görseller oluşturur.',
    prompt: 'Sen bir görsel düzenleme asistanısın...' // Placeholder prompt
  },
  {
    name: 'Emlak Danışmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen deneyimli bir emlak danışmanısın. Kullanıcılara Türkiye\'deki emlak piyasası hakkında bilgi ver, alım, satım ve kiralama süreçlerinde rehberlik et. Güncel piyasa trendleri ve fiyat analizleri sun.',
    description: 'Emlak alım, satım ve kiralama süreçleri ile piyasa bilgileri konusunda uzman.',
    prompt: 'Sen bir emlak danışmanısın...'
  },
  {
    name: 'Konut Kredisi Uzmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen bir konut kredisi uzmanısın. Kullanıcılara Türkiye\'deki bankaların konut kredisi seçenekleri, faiz oranları, başvuru süreçleri ve geri ödeme planları hakkında detaylı bilgi ver.',
    description: 'Konut kredileri, faiz oranları ve başvuru süreçleri hakkında bilgi verir.',
    prompt: 'Sen bir konut kredisi uzmanısın...'
  },
  {
    name: 'Tapu ve Kadastro Uzmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen tapu ve kadastro işlemleri konusunda uzman bir danışmansın. Kullanıcılara tapu devri, ipotek işlemleri, kadastro sorgulamaları ve ilgili yasal süreçler hakkında bilgi ver.',
    description: 'Tapu, kadastro, ipotek ve ilgili yasal işlemler konusunda uzman.',
    prompt: 'Sen tapu ve kadastro uzmanısın...'
  },
  {
    name: 'Gayrimenkul Değerleme Uzmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen lisanslı bir gayrimenkul değerleme uzmanısın. Kullanıcılara bir mülkün değerini etkileyen faktörleri, değerleme yöntemlerini ve raporlama süreçlerini açıkla.',
    description: 'Mülk değerlemesi, yöntemleri ve etkileyen faktörler hakkında bilgi verir.',
    prompt: 'Sen bir gayrimenkul değerleme uzmanısın...'
  },
  {
    name: 'Kentsel Dönüşüm Danışmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen kentsel dönüşüm süreçleri konusunda uzman bir danışmansın. Kullanıcılara Türkiye\'deki kentsel dönüşüm projeleri, yasal düzenlemeler, hak sahipliği ve teşvikler hakkında bilgi ver.',
    description: 'Kentsel dönüşüm projeleri, yasaları ve hak sahipliği konularında uzman.',
    prompt: 'Sen bir kentsel dönüşüm danışmanısın...'
  },
  {
    name: 'Yatırım Danışmanı (Gayrimenkul)',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen gayrimenkul yatırımları konusunda uzman bir danışmansın. Kullanıcılara yatırım stratejileri, potansiyel getiriler, risk analizleri ve piyasa fırsatları hakkında bilgi ver.',
    description: 'Gayrimenkul yatırım stratejileri, analizleri ve fırsatları konusunda uzman.',
    prompt: 'Sen bir gayrimenkul yatırım danışmanısın...'
  },
  {
    name: 'Emlak Hukuku Danışmanı',
    type: AGENT_TYPE_TEXT,
    model: TEXT_MODEL,
    systemInstruction: 'Sen emlak hukuku alanında uzman bir danışmansın. Kullanıcılara kira sözleşmeleri, alım-satım sözleşmeleri, tapu davaları ve diğer hukuki konular hakkında bilgi ver. (Not: Bu tavsiye yasal danışmanlık yerine geçmez.)',
    description: 'Emlak ile ilgili hukuki süreçler, sözleşmeler ve anlaşmazlıklar hakkında bilgi verir.',
    prompt: 'Sen bir emlak hukuku danışmanısın...'
  },
  // Add a default/generic agent for image generation if needed, or handle null agent case
  // {
  //   name: 'Default Image Generator', // Example if you want to keep image generation accessible
  //   type: AGENT_TYPE_IMAGE,
  //   model: IMAGE_MODEL,
  //   systemInstruction: '', // No specific system instruction for default image gen
  //   description: 'Generates images based on your prompts.',
  //   prompt: ''
  // }
];

const AgentMemory = ({ session, currentAgent, setCurrentAgent }) => {
  const [customAgents, setCustomAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [customAgentName, setCustomAgentName] = useState('');
  const [customAgentDescription, setCustomAgentDescription] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState(null);

  useEffect(() => {
    if (session) {
      fetchCustomAgents();
    }
  }, [session]);

  const fetchCustomAgents = async () => {
    try {
      // Check if custom_agents table exists
      const tableExists = await ensureCustomAgentsTableExists();
      
      if (!tableExists) {
        console.warn('Custom agents table does not exist');
        setCustomAgents([]);
        return;
      }
      
      const { data, error } = await supabase
        .from('custom_agents')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) {
        console.error('Error fetching custom agents:', error);
        // Always use empty array on error to prevent UI crashes
        setCustomAgents([]);
      } else {
        setCustomAgents(data || []);
      }
    } catch (error) {
      console.error('Error initializing custom agents:', error);
      setCustomAgents([]);
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent.name);
    // Pass the full agent object, including type, model, and systemInstruction
    setCurrentAgent(agent);
  };

  const handleEditAgent = (agent) => {
    setAgentToEdit(agent);
    setCustomAgentName(agent.name);
    setCustomPrompt(agent.prompt);
    setCustomAgentDescription(agent.description || '');
    setEditDialogOpen(true);
  };

  const handleDeleteAgent = (agent) => {
    setAgentToEdit(agent);
    setDeleteDialogOpen(true);
  };

  const handleSaveCustomAgent = async () => {
    if (!customPrompt.trim() || !customAgentName.trim() || !session) {
      return;
    }

    const newAgent = {
      name: customAgentName,
      prompt: customPrompt, // Keep prompt for now
      description: customAgentDescription || 'Custom agent',
      user_id: session.user.id,
      // For custom agents, default to text type for now. UI could be added later.
      type: AGENT_TYPE_TEXT,
      model: TEXT_MODEL,
      systemInstruction: customPrompt, // Use the prompt field as system instruction for custom agents
    };

    if (agentToEdit && customAgents.some(a => a.id === agentToEdit.id)) {
      // Update existing custom agent
      const { error } = await supabase
        .from('custom_agents')
        .update({ 
          name: customAgentName,
          prompt: customPrompt, // Update prompt field
          description: customAgentDescription,
          // Also update system instruction when prompt changes for custom agents
          systemInstruction: customPrompt
        })
        .eq('id', agentToEdit.id);

      if (error) {
        console.error('Error updating custom agent:', error);
      } else {
        fetchCustomAgents();
        if (currentAgent && currentAgent.id === agentToEdit.id) {
          setCurrentAgent(newAgent);
        }
      }
    } else {
      // Create new custom agent
      const { error } = await supabase
        .from('custom_agents')
        .insert([newAgent]);

      if (error) {
        console.error('Error saving custom agent:', error);
      } else {
        fetchCustomAgents();
      }
    }
    
    setEditDialogOpen(false);
    setAgentToEdit(null);
    setCustomAgentName('');
    setCustomPrompt('');
    setCustomAgentDescription('');
  };

  const confirmDeleteCustomAgent = async () => {
    if (!agentToEdit || !session) {
      setDeleteDialogOpen(false);
      return;
    }

    const { error } = await supabase
      .from('custom_agents')
      .delete()
      .eq('id', agentToEdit.id);

    if (error) {
      console.error('Error deleting custom agent:', error);
    } else {
      if (currentAgent && currentAgent.id === agentToEdit.id) {
        setCurrentAgent(null);
      }
      fetchCustomAgents();
    }
    
    setDeleteDialogOpen(false);
    setAgentToEdit(null);
  };

  // Combine predefined and custom agents
  const allAgents = [...predefinedAgents, ...customAgents];

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            AI Asistanlarınız
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <Avatar sx={{ width: 16, height: 16, bgcolor: 'info.light', mr: 0.5 }}>
                <BrushIcon sx={{ fontSize: '0.6rem' }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary">Görsel</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 16, height: 16, bgcolor: 'success.light', mr: 0.5 }}>
                <DescriptionIcon sx={{ fontSize: '0.6rem' }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary">Metin</Typography>
            </Box>
          </Box>
        </Box>
        {/* Removed "Tüm asistanlar" link */}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        overflowX: 'auto', 
        pb: 1,
        '&::-webkit-scrollbar': {
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        {allAgents.slice(0, 8).map((agent, index) => (
          <Card 
            key={index} 
            sx={{ 
              minWidth: 220, 
              maxWidth: 220,
              mr: 2, 
              cursor: 'pointer',
              border: currentAgent && currentAgent.name === agent.name ? '2px solid' : '1px solid',
              borderColor: currentAgent && currentAgent.name === agent.name ? 'primary.main' : 'divider',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 3,
                borderColor: 'primary.light',
              }
            }}
            onClick={() => handleAgentSelect(agent)}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.light', 
                    width: 36, 
                    height: 36,
                    mr: 1.5
                  }}
                >
                  {agentIcons[agent.name] || <PersonIcon />}
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {agent.name}
                  </Typography>
                  <Tooltip title={agent.type === 'image' ? 'Görsel çıktı üretir' : 'Metin çıktı üretir'}>
                    <Avatar 
                      sx={{ 
                        ml: 1, 
                        width: 20, 
                        height: 20, 
                        bgcolor: agent.type === 'image' ? 'info.light' : 'success.light',
                        fontSize: '0.75rem'
                      }}
                    >
                      {agent.type === 'image' ? <BrushIcon fontSize="inherit" /> : <DescriptionIcon fontSize="inherit" />}
                    </Avatar>
                  </Tooltip>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ 
                height: 40, 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {agent.description || 'Çeşitli görevler için AI asistanı'}
              </Typography>
              
              {customAgents.some(a => a.name === agent.name) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAgent(agent);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAgent(agent);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
        
        <Card 
          sx={{ 
            minWidth: 220,
            maxWidth: 220,
            mr: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            }
          }}
          onClick={() => {
            setAgentToEdit(null);
            setCustomAgentName('');
            setCustomPrompt('');
            setCustomAgentDescription('');
            setEditDialogOpen(true);
          }}
        >
          <CardContent sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}>
            <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Yeni asistan oluştur
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Edit/Create Agent Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {agentToEdit ? 'Asistanı Düzenle' : 'Yeni Asistan Oluştur'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Asistan Adı"
            fullWidth
            variant="outlined"
            value={customAgentName}
            onChange={(e) => setCustomAgentName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Açıklama"
            fullWidth
            variant="outlined"
            value={customAgentDescription}
            onChange={(e) => setCustomAgentDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Sistem Promptu"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleSaveCustomAgent} variant="contained">
            {agentToEdit ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Silme İşlemini Onayla</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{agentToEdit?.name}" asistanını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={confirmDeleteCustomAgent} color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentMemory;
